import { computed, Ref } from "vue";
import { ExpressionSpecification } from "mapbox-gl";
import { PlantingDTO } from "seedgreen-shared/dtos/PlantingDTO";
import { BBox, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";
import { bbox, centerOfMass } from "@turf/turf";
import {
	DEFAULT_CLUSTER_COUNT_LAYOUT,
	DEFAULT_CLUSTER_COUNT_PAINT,
	DEFAULT_CLUSTER_PAINT_SETTINGS,
	MAP_LABEL_DEFAULT_PAINT,
	MAP_LABEL_DEFAULT_SIZE,
	MAP_LABEL_FONT,
} from "@/constants/map.constants";
import { getDynamicMapPadding } from "@/utils/map";
import { usePlantingsLayerInteraction } from "@/composables/map/usePlantingsLayerInteraction";
import { captureException } from "@sentry/vue";

/**
 * Foreground - full opacity, interactible.
 * Faded - lower opacity, interactible.
 * Background - lower opacity, not interactible.
 * Hidden - not visible, not interactible.
 */
export type PlantingLayerMode =
	| "foreground"
	| "faded"
	| "background"
	| "hidden";

const PLANTING_FOREGROUND_FILL: ExpressionSpecification = [
	"case",
	["==", ["get", "status"], "planted"],
	"#71ba34",
	["==", ["get", "status"], "active"],
	"#567e91",
	["boolean", ["feature-state", "selected"], false],
	"#4CAF50",
	// Idea for later:
	// ["==", ["get", "status"], "upcoming"],
	// "grey",
	// ["==", ["get", "status"], "harvested"],
	// "#ccb481",
	"gray",
];
const PLANTING_FOREGROUND_LINE_COLOR = PLANTING_FOREGROUND_FILL;
const PLANTING_LINE_WIDTH = 2;
const PLANTING_FOREGROUND_OPACITY = 0.3;
const PLANTING_FADED_OPACITY = 0.1;
const PLANTING_BACKGROUND_OPACITY = 0.05;

export interface PlantingsLayerOptions {
	plantingsCluserRadius?: number;
	plantingsClusterZoomCrossover?: number;
	plantingLabelsMinZoom?: number;
}
export default function usePlantingsLayer(
	map: Ref<mapboxgl.Map | null>,
	plantings: Ref<PlantingDTO[]>,
	clickCallback: (plantingIds: number[]) => void,
	options?: PlantingsLayerOptions,
) {
	const { disablePlantingsInteraction, enablePlantingsInteraction } =
		usePlantingsLayerInteraction(map, clickCallback);

	// Data
	const plantingCentersGeoJson = computed<FeatureCollection<Point>>(() =>
		getPlantingCentersGJ(plantings.value),
	);
	const plantingBoundariesGeoJson = computed<
		FeatureCollection<Polygon | MultiPolygon>
	>(() => getPlantingBoundariesGJ(plantings.value));

	function initPlantingsSources() {
		map.value?.addSource("planting-centers", {
			type: "geojson",
			data: plantingCentersGeoJson.value,
			cluster: true,
			clusterMaxZoom: (options?.plantingsClusterZoomCrossover ?? 9) - 1,
			clusterRadius: options?.plantingsCluserRadius ?? 50,
		});

		map.value?.addSource("plantings-boundaries", {
			type: "geojson",
			data: plantingBoundariesGeoJson.value,
			minzoom: options?.plantingsClusterZoomCrossover ?? 9,
		});
	}

	function initPlantingsLayers() {
		if (!map.value) {
			console.error("Map not initialized");
			captureException(new Error("Map not initialized"));
			return;
		}

		initPolygonLayers();
		initCentersLayers();
	}

	function initCentersLayers() {
		map.value?.addLayer({
			id: "planting-clusters",
			type: "circle",
			source: "planting-centers",
			filter: ["has", "point_count"],
			paint: DEFAULT_CLUSTER_PAINT_SETTINGS,
		});

		map.value?.addLayer({
			id: "planting-cluster-count",
			type: "symbol",
			source: "planting-centers",
			layout: DEFAULT_CLUSTER_COUNT_LAYOUT,
			paint: DEFAULT_CLUSTER_COUNT_PAINT,
		});

		map.value?.addLayer({
			id: "planting-labels",
			type: "symbol",
			source: "planting-centers",
			minzoom: options?.plantingLabelsMinZoom ?? 13,
			filter: ["!", ["has", "point_count"]],
			layout: {
				"text-field": ["get", "plantingName"],
				"text-font": MAP_LABEL_FONT,
				"text-size": MAP_LABEL_DEFAULT_SIZE,
			},
			paint: MAP_LABEL_DEFAULT_PAINT,
		});

		// inspect a cluster on click
		map.value?.on("click", "planting-clusters", (e) => {
			const features = map.value?.queryRenderedFeatures(e.point, {
				layers: ["planting-clusters"],
			});
			if (!features) return;
			const clusterId = features[0].properties?.cluster_id;

			const source = map.value?.getSource(
				"planting-centers",
			) as mapboxgl.GeoJSONSource;

			if (!source) return;

			source.getClusterExpansionZoom(clusterId, (err, zoom) => {
				if (err) return;

				map.value?.easeTo({
					center: (features[0].geometry as Point).coordinates as [
						number,
						number,
					],
					zoom: zoom ?? undefined,
				});
			});
		});

		map.value?.on("mouseenter", "planting-clusters", () => {
			if (map.value) map.value.getCanvas().style.cursor = "pointer";
		});
		map.value?.on("mouseleave", "planting-clusters", () => {
			if (map.value) map.value.getCanvas().style.cursor = "";
		});
	}

	function initPolygonLayers() {
		map.value?.addLayer({
			id: "plantings-boundaries",
			type: "line",
			source: "plantings-boundaries",
			paint: {
				"line-color": PLANTING_FOREGROUND_LINE_COLOR,
				"line-width": PLANTING_LINE_WIDTH,
			},
		});

		map.value?.addLayer({
			id: "plantings-fill",
			type: "fill",
			source: "plantings-boundaries",
			paint: {
				"fill-color": PLANTING_FOREGROUND_FILL,
				"fill-opacity": plantingsFillOpacityExpression(
					PLANTING_FOREGROUND_OPACITY,
					PLANTING_FOREGROUND_OPACITY * 1.25,
				),
			},
		});
	}

	function refreshPlantingsData() {
		if (!map.value) return;

		(
			map.value.getSource("planting-centers") as mapboxgl.GeoJSONSource
		).setData(plantingCentersGeoJson.value);
		(
			map.value.getSource(
				"plantings-boundaries",
			) as mapboxgl.GeoJSONSource
		).setData(plantingBoundariesGeoJson.value);
	}

	/** Center around all displayed plantings */
	function centerMapOnPlantings() {
		if (!map.value || !plantings.value?.length) {
			return;
		}

		let plantingsBbox: BBox;
		if (plantings.value.length < 50) {
			plantingsBbox = bbox(plantingBoundariesGeoJson.value);
		} else {
			plantingsBbox = bbox(plantingCentersGeoJson.value);
		}

		map.value.fitBounds(
			[
				[plantingsBbox[0], plantingsBbox[1]], // Southwest corner
				[plantingsBbox[2], plantingsBbox[3]], // Northeast corner
			],
			{
				padding: getDynamicMapPadding(map.value),
				duration: 500,
			},
		);
	}

	/** Center on a single planting */
	function centerMapOnPlanting(plantingId: number) {
		if (!map.value || !plantings.value?.length) {
			return;
		}

		const planting = plantings.value.find((p) => p.id === plantingId);
		if (!planting || !planting.latestBoundary) {
			return;
		}

		const plantingBbox = bbox(planting.latestBoundary);

		map.value.fitBounds(
			[
				[plantingBbox[0], plantingBbox[1]], // Southwest corner
				[plantingBbox[2], plantingBbox[3]], // Northeast corner
			],
			{
				padding: getDynamicMapPadding(map.value),
				screenSpeed: 4,
			},
		);
	}

	function setPlantingCentersVisibility(visible: boolean) {
		if (!map.value) return;

		const visibility = visible ? "visible" : "none";
		map.value.setLayoutProperty(
			"planting-clusters",
			"visibility",
			visibility,
		);
		map.value.setLayoutProperty(
			"planting-cluster-count",
			"visibility",
			visibility,
		);
		map.value.setLayoutProperty(
			"planting-labels",
			"visibility",
			visibility,
		);
	}

	function setPlantingBoundariesVisibility(visible: boolean) {
		if (!map.value) return;

		const visibility = visible ? "visible" : "none";
		map.value.setLayoutProperty(
			"plantings-boundaries",
			"visibility",
			visibility,
		);
		map.value.setLayoutProperty("plantings-fill", "visibility", visibility);
	}

	function setPlantingsPaint(mode: PlantingLayerMode) {
		if (!map.value) return;

		const opacityByMode = {
			foreground: plantingsFillOpacityExpression(
				PLANTING_FOREGROUND_OPACITY,
				PLANTING_FOREGROUND_OPACITY * 1.25,
			),
			faded: plantingsFillOpacityExpression(
				PLANTING_FADED_OPACITY,
				PLANTING_FADED_OPACITY * 1.25,
			),
			background: plantingsFillOpacityExpression(
				PLANTING_BACKGROUND_OPACITY,
				PLANTING_BACKGROUND_OPACITY,
			),
			hidden: 0,
		};

		map.value.setPaintProperty(
			"plantings-fill",
			"fill-opacity",
			opacityByMode[mode],
		);

		map.value.setPaintProperty(
			"plantings-boundaries",
			"line-opacity",
			opacityByMode[mode],
		);
	}

	/** Set the display mode (appearance) for the plantings layer */
	function setPlantingsLayerMode(mode: PlantingLayerMode) {
		if (!map.value) return;

		if (mode === "foreground") {
			setPlantingCentersVisibility(true);
			setPlantingBoundariesVisibility(true);
			enablePlantingsInteraction();
		} else if (mode === "faded") {
			setPlantingCentersVisibility(true);
			setPlantingBoundariesVisibility(true);
			enablePlantingsInteraction();
		} else if (mode === "background") {
			setPlantingCentersVisibility(false);
			setPlantingBoundariesVisibility(true);
			disablePlantingsInteraction();
		} else if (mode == "hidden") {
			setPlantingCentersVisibility(false);
			setPlantingBoundariesVisibility(false);
			disablePlantingsInteraction();
		}

		setPlantingsPaint(mode);
	}

	return {
		initPlantingsSources,
		initPlantingsLayers,
		refreshPlantingsData,
		setPlantingsLayerMode,
		centerMapOnPlantings,
		centerMapOnPlanting,
	};
}

/**
 * Get GeoJSON FeatureCollection of Planting centers (center of mass)
 * @param data
 * @returns
 */
function getPlantingCentersGJ(data: PlantingDTO[]): FeatureCollection<Point> {
	return {
		type: "FeatureCollection",
		features: data
			.filter((p) => p.latestBoundary)
			.map((p) => {
				const center = centerOfMass(
					p.latestBoundary as Polygon | MultiPolygon,
				);
				return {
					id: p.id,
					type: center.type,
					geometry: center.geometry,
					properties: {
						plantingName: p.name,
					},
				};
			}),
	};
}

/**
 * Get GeoJSON FeatureCollection of Planting boundaries
 * @param data
 * @returns
 */
function getPlantingBoundariesGJ(
	data: PlantingDTO[],
): FeatureCollection<Polygon | MultiPolygon> {
	return {
		type: "FeatureCollection",
		features: data
			.filter((p) => p.latestBoundary)
			.map((p) => ({
				id: p.id,
				type: "Feature",
				geometry: p.latestBoundary as Polygon | MultiPolygon,
				properties: {
					status: p.status,
				},
			})),
	};
}

function plantingsFillOpacityExpression(
	baseOpacity: number,
	hoverOpacity: number,
): ExpressionSpecification {
	return [
		"case",
		["boolean", ["feature-state", "hover"], false],
		hoverOpacity,
		baseOpacity,
	];
}
